

import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  useTheme,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  FormHelperText,
  CircularProgress,
  Snackbar, // ADDED
  Alert,      // ADDED
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material'; // ADDED
import { gql, useMutation } from '@apollo/client';

// Make sure the path to your 'formicon.svg' is correct.
const FORM_ICON_URL = `${process.env.PUBLIC_URL}/formicon.svg`;

// Defines the structure of data for the user being edited
interface UserData {
  id: string;
  name: string;
  email: string;
  currentRole: string;
}

// Defines the props expected by the EditUserForm component
interface EditUserFormProps {
  open: boolean;
  onClose: () => void;
  onUserUpdate: (userId: string, newRole: string) => void;
  userData: UserData | null;
}

// GraphQL Mutation for changing a user's role
const CHANGE_TENANT_MEMBERSHIP_ROLE = gql`
  mutation ChangeTenantMembershipRole($tenantMembershipId: ID!, $roleInTenant: Role!) {
    changeTenantMembershipRole(tenantMembershipId: $tenantMembershipId, roleInTenant: $roleInTenant) {
      id
      roleInTenant
      user {
        id
        name
        email
      }
    }
  }
`;

// Enum for Role, ensure this matches your GraphQL schema
enum RoleGraphQLEnum {
  Admin = 'admin',
  Editor = 'editor',
  Viewer = 'viewer',
}

const roleDotColors: { [key: string]: string } = {
  Admin: '#FF9900',
  Editor: '#0064FF',
  Viewer: '#6C737F',
};

// Custom icon component for the error toast
const CustomErrorIcon = () => (
    <Box
      component="span"
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#D92D20',
      }}
    >
      <CloseIcon sx={{ color: 'white', fontSize: '16px' }} />
    </Box>
);


const EditUserForm: React.FC<EditUserFormProps> = ({
  open,
  onClose,
  onUserUpdate,
  userData,
}) => {
  const theme = useTheme();

  const [role, setRole] = useState('');
  const [roleError, setRoleError] = useState('');
  
  // ADDED: State for toast notifications
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);

  const [changeTenantMembershipRoleMutation, { loading: isUpdatingUser }] = useMutation(
    CHANGE_TENANT_MEMBERSHIP_ROLE,
    {
      onCompleted: (data) => {
        onUserUpdate(userData!.id, role);
        onClose();
      },
      // MODIFIED: onError now triggers the toast with custom messages
      onError: (error) => {
        const serverMessage = error.graphQLErrors?.[0]?.message;
        const errorMap: { [key: string]: string } = {
            // Add any specific server errors you want to customize here
        };
        const customMessage = errorMap[serverMessage] || serverMessage || 'We are unable to update the user role at this moment. Please try again after some time.';
        setToastMessage(customMessage);
        setIsToastOpen(true);
      },
    }
  );

  useEffect(() => {
    if (open && userData) {
      const displayRole = userData.currentRole.charAt(0).toUpperCase() + userData.currentRole.slice(1);
      setRole(displayRole);
      setRoleError('');
      setToastMessage(null); // Reset toast
    }
    if (!open) {
        setRole('');
        setRoleError('');
        setToastMessage(null); // Reset toast
    }
  }, [open, userData]);

  const validateRole = (currentRole: string): string => {
    if (!currentRole.trim()) return 'Role is required.';
    return '';
  };

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const val = event.target.value as string;
    setRole(val);
    if (roleError) {
      setRoleError(validateRole(val));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setToastMessage(null);

    if (!userData) {
      setToastMessage('User data is missing. Cannot update role.');
      setIsToastOpen(true);
      return;
    }

    const currentRoleError = validateRole(role);
    setRoleError(currentRoleError);

    if (currentRoleError) {
      return;
    }
    
    const roleInTenantForMutation = role.toLowerCase() as RoleGraphQLEnum;

    try {
      await changeTenantMembershipRoleMutation({
        variables: {
          tenantMembershipId: userData.id,
          roleInTenant: roleInTenantForMutation,
        },
      });
    } catch (err) {
       // Error is handled by the onError callback
    }
  };

  // ADDED: Handler to close the toast
  const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsToastOpen(false);
  };

  const isSaveChangesButtonDisabled =
    !role.trim() ||
    isUpdatingUser ||
    (userData?.currentRole.toLowerCase() === role.toLowerCase());

  const commonTextFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '& fieldset': { borderColor: 'rgba(232, 235, 236, 1)' },
      '&:hover fieldset': { borderColor: theme.palette.grey[500] },
      '& .MuiInputBase-input': {
        fontSize: '13px',
    },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
         color: theme.palette.text.secondary,
        '& fieldset': { borderColor: 'rgba(232, 235, 236, 0.5)' },
        '.MuiOutlinedInput-input': {
           color: theme.palette.text.secondary,
           WebkitTextFillColor: theme.palette.text.secondary,
        },
      },
    },
     '& .MuiInputBase-input::placeholder': {
        fontSize: '14px', opacity: 1, color: 'rgba(185, 185, 185, 1)', fontFamily: 'Inter'
    },
  };

  const externalLabelSx = {
    mb: 0.5, fontWeight: 400, fontSize: '12px', color: 'rgba(51, 51, 51, 1)', fontFamily: 'Inter'
  };
  
  const availableRoles = Object.keys(roleDotColors);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit,
          sx: {
            borderRadius: '16px',
            maxWidth: '600px',
          }
        }}
        BackdropProps={{ sx: { backdropFilter: 'blur(5px)' } }}
        fullWidth
        sx={{ zIndex: 1301 }}
      >
        <DialogTitle sx={{
          p: theme.spacing(2, 3),
          backgroundImage: `
            linear-gradient(to bottom, transparent 80%, white 100%),
            url('/blue-bg-form.svg')
          `,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          borderTopRightRadius: '16px',
          borderTopLeftRadius: '16px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box 
                component="span" 
                sx={{ 
                  mt: 3, 
                  width: 48, 
                  height: 48, 
                  borderRadius: '8px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  mr: 1.5, 
                  backgroundColor:'#E5EFFF', 
                  boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)' 
                }}
              >
                <img src={FORM_ICON_URL} alt="Form Icon" style={{ width: '24px'}} />
              </Box>
              <Box>
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    fontFamily:'Inter',
                    fontWeight: 600, 
                    fontSize: '16px',
                    mb:0.3, 
                    mt: 3,
                    color: 'rgba(3, 23, 52, 1)'
                  }}
                >
                  Edit User
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    fontFamily:'Inter',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'rgba(71, 84, 103, 1)'
                  }}
                >
                  Update the below details to edit user.
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* REMOVED the old inline formError Typography component */}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 2 } , mb: 2.5, mt: 5}}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <InputLabel htmlFor="edit-fullName" sx={externalLabelSx}>
                Name
              </InputLabel>
              <TextField
                fullWidth
                id="edit-fullName"
                type="text"
                variant="outlined"
                size="small"
                value={userData?.name || ''}
                disabled
                sx={commonTextFieldSx}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <InputLabel htmlFor="edit-email" sx={externalLabelSx}>
                Email
              </InputLabel>
              <TextField
                fullWidth
                id="edit-email"
                type="email"
                variant="outlined"
                size="small"
                value={userData?.email || ''}
                disabled
                sx={commonTextFieldSx}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 0.5, width: { xs: '100%', sm: `calc(50% - ${theme.spacing(1)})` } }}>
              <InputLabel htmlFor="edit-role" sx={externalLabelSx}>
                  Role <span style={{ color: theme.palette.error.main }}>*</span>
              </InputLabel>
              <FormControl fullWidth error={!!roleError}>
                  <Select
                      id="edit-role"
                      value={role}
                      onChange={handleRoleChange}
                      displayEmpty
                      variant="outlined"
                      size="small"
                      disabled={isUpdatingUser}
                      sx={{ ...commonTextFieldSx, borderRadius: '8px', '& fieldset': { borderColor: '#E8EBEC' }, '& .MuiSelect-select': { display: 'flex', alignItems: 'center' } }}
                      renderValue={(selectedValue) => {
                          if (!selectedValue) {
                              return <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: 'rgba(185, 185, 185, 1)' }}>Select role</Typography>;
                          }
                          const dotColor = roleDotColors[selectedValue] || theme.palette.action.disabled;
                          return (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, mr: 1 }}/>
                                  <Typography component="span" sx={{ color: theme.palette.text.primary,fontSize:'14px' }}>{selectedValue}</Typography>
                              </Box>
                          );
                      }}
                      MenuProps={{
                          sx: {
                              zIndex: 1400,
                          },
                      }}
                  >
                      {availableRoles.map((roleOption) => (
                          <MenuItem key={roleOption} value={roleOption} sx={{ fontSize: '13px' }} >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: roleDotColors[roleOption], mr: 1, fontSize: '13px' }} />
                                  {roleOption}
                              </Box>
                          </MenuItem>
                      ))}
                  </Select>
                  {roleError && <FormHelperText>{roleError}</FormHelperText>}
              </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: theme.spacing(2, 3) }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isUpdatingUser}
            sx={{
              width: '120px',
              borderRadius: '8px', color: 'rgba(52, 64, 84, 1)', borderColor: 'rgba(208, 213, 221, 1)',
              '&:hover': { borderColor: theme.palette.grey[700], backgroundColor: 'action.hover' },
              py: '8px', textTransform: 'none', fontWeight: 500, fontSize: '14px', fontFamily:'Inter',
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSaveChangesButtonDisabled}
            sx={{
              width: '140px',
              py: '8px', borderRadius:'8px', textTransform: 'none',
              fontWeight: 500, fontSize: '14px', fontFamily:'Inter', backgroundColor: '#0064FF',
              '&.Mui-disabled': {
                  backgroundColor: 'rgba(224, 224, 224, 1)',
                  color: 'rgba(173, 173, 173, 1)'
              }
            }}
            startIcon={isUpdatingUser ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isUpdatingUser ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ADDED: Snackbar with custom styled Alert for error toast */}
      <Snackbar
        open={isToastOpen}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          icon={<CustomErrorIcon />}
          onClose={handleToastClose}
          sx={{
            width: '100%',
            borderRadius: '50px',
            backgroundColor: 'white',
            boxShadow: theme.shadows[3],
            color: 'text.secondary',
            alignItems: 'center',
            p: theme.spacing(0.5, 2),
            '& .MuiAlert-message': {
              flex: 1,
              py: '12px'
            },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditUserForm;
